import { useQuery, useQueryClient } from '@tanstack/react-query';
import i18n from 'i18next';
import { supabase } from '@/integrations/supabase/client';
import { getStoredReferralCode } from '@/lib/referral';

export type SubscriptionTier = 'starter' | 'professional' | 'business' | 'internal' | null;

/** Raw Stripe-derived status. 'past_due'/'unpaid'/'incomplete' = dunning. */
export type SubscriptionStatus =
  | 'active' | 'trialing' | 'past_due' | 'unpaid' | 'incomplete'
  | 'incomplete_expired' | 'paused' | 'canceled' | null;

export interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: SubscriptionTier;
  subscription_end: string | null;
  is_trialing: boolean;
  status: SubscriptionStatus;
  bypass_subscription: boolean;
  /** Staff-granted AI receptionist add-on (separate purchase, not a tier). */
  ai_addon: boolean;
  /** Rollout gate, not a purchase. See 20260830100000. */
  estimates_enabled: boolean;
  /** True when the backend served last-known DB state because Stripe errored. */
  stale: boolean;
  loading: boolean;
}

// check-subscription is DB-first: without `force` it serves the subscriptions
// row synced within the last 5 minutes (zero Stripe calls). `force: true`
// makes it reconcile with Stripe — only for flows that need real-time truth
// (post-checkout return), never for regular page loads.
async function fetchSubscription(force = false): Promise<Omit<SubscriptionData, 'loading'>> {
  const { data, error } = await supabase.functions.invoke(
    'check-subscription',
    force ? { body: { force: true } } : undefined,
  );
  if (error) throw error;
  return {
    subscribed: data.subscribed ?? false,
    subscription_tier: data.subscription_tier ?? null,
    subscription_end: data.subscription_end ?? null,
    is_trialing: data.is_trialing ?? false,
    status: data.status ?? null,
    bypass_subscription: data.bypass_subscription ?? false,
    ai_addon: data.ai_addon ?? false,
    estimates_enabled: data.estimates_enabled ?? false,
    stale: data.stale ?? false,
  };
}

export type BillingInterval = 'monthly' | 'annual';

async function createCheckoutSession(
  plan: string,
  interval: BillingInterval = 'monthly',
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('create-checkout', {
    body: {
      plan,
      interval,
      // THE RETRY PATH. A referred customer who abandoned the card screen at
      // signup comes back through this paywall days later, and until now the
      // request carried nothing but the plan — so the partner's sale silently
      // evaporated at the exact moment it finally paid. The server-side rescue
      // (the attribution business-signup now opens before Checkout) is the
      // primary fix; this covers the case where the browser still remembers and
      // the DB row was never written.
      referralCode: getStoredReferralCode() ?? undefined,
      // Stripe otherwise picks the payment screen's language from the browser,
      // not from the language the user has been reading the dashboard in.
      locale: i18n.language,
    },
  });
  if (error) throw error;
  if (!data?.url) throw new Error('No checkout URL returned');
  return data.url;
}

export function useSubscription(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const { data, isLoading, error } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => fetchSubscription(),
    staleTime: 1000 * 60 * 5, // 5 min cache
    retry: 1,
    // Gate the fetch on auth-readiness from callers (e.g. route guards) so we
    // don't fire check-subscription before a session exists and cache a 401.
    enabled,
  });

  // `force: true` bypasses the backend's DB cache and reconciles with Stripe
  // directly — required after Checkout returns, where the 5-minute-old DB row
  // would still say "canceled" and bounce a freshly paying user off the app.
  const refresh = async (opts?: { force?: boolean }) => {
    if (opts?.force) {
      try {
        const fresh = await fetchSubscription(true);
        queryClient.setQueryData(['subscription'], fresh);
        return;
      } catch {
        // Fall through to a normal refetch — worst case we serve DB state.
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['subscription'] });
  };

  return {
    subscribed: data?.subscribed ?? false,
    subscription_tier: data?.subscription_tier ?? null,
    subscription_end: data?.subscription_end ?? null,
    is_trialing: data?.is_trialing ?? false,
    status: data?.status ?? null,
    bypass_subscription: data?.bypass_subscription ?? false,
    ai_addon: data?.ai_addon ?? false,
    estimates_enabled: data?.estimates_enabled ?? false,
    stale: data?.stale ?? false,
    loading: isLoading,
    error,
    refresh,
    createCheckout: createCheckoutSession,
  };
}
